import { agentDisplayMap } from '@/components/Agents/AgentUtils';
import { _isNil, _notNil } from '@/littledash';
import type { Edge } from '@xyflow/react';
import { RiCheckboxCircleLine, RiCloseCircleLine, RiLoader4Line } from 'react-icons/ri';
import type {
  GraphCanvasConfig,
  GraphCanvasNode,
  GraphCanvasSnapshot,
  SiblingComparator,
} from '@/components/UI/GraphCanvas/GraphCanvas.model';
import { treeLayout } from '@/components/UI/GraphCanvas/GraphCanvas.utils';
import type { ButtonNodeData, GraphNodeBadge, ReadOnlyNodeData } from '@/components/UI/GraphCanvas/nodes/Nodes.utils';
import type {
  AgentSessionApiId,
  AgentSessionStatus,
  AgentStreamEventV1,
  AgentTreeNodeApiId,
  AgentTreeNodeType,
  AgentTreeNodeV1,
  AgentTreeSessionV1,
  AgentTreeV1,
  AgentType,
} from '@/model/Agent.model';
import { StringUtils } from '@/utils/String.utils';

/** The `tree_node_created` variant of the SSE stream union — carries everything to build a node incrementally. */
type TreeNodeCreatedEvent = Extract<AgentStreamEventV1, { type: 'tree_node_created' }>;

export type { AgentSessionStatus, AgentTreeNodeType };

/**
 * Derivation metadata carried on every tree node's `data`, independent of how the node renders. Edge
 * derivation reads `parentApiId`; sibling ordering reads `sequenceNumber`; `type` is the node's
 * structural position in the tree (root/result). Kept alongside the render payload because the
 * GraphCanvas engine derives edges/layout purely from node `data`. `variant` is NOT here — it is the
 * render discriminant and lives on each union arm below.
 */
interface AgentTreeNodeBase extends Record<string, unknown> {
  apiId: AgentTreeNodeApiId;
  parentApiId: AgentTreeNodeApiId | null;
  type: AgentTreeNodeType;
  sequenceNumber: number;
}

/**
 * The `TData` carried on each React Flow node: derivation metadata plus the generic render payload for
 * whichever GraphCanvas node component renders it. Discriminated on `variant` — the same field that
 * drives node-component routing (`node.type = variant`) — so the payload and the rendered component
 * cannot drift: `read_only` carries a `ReadOnlyNode` payload (session title + status badge), `report`
 * a `ButtonNode` payload ("View Report"). Agent type/status are folded into the payload at map time,
 * so nothing agent-specific reaches the node components.
 */
export type AgentTreeNodeData =
  | (AgentTreeNodeBase & { variant: 'read_only' } & ReadOnlyNodeData)
  | (AgentTreeNodeBase & { variant: 'report' } & ButtonNodeData);

/** Session-level metadata held as the engine's `TMeta`; drives the surrounding page chrome. */
export interface AgentSessionMeta extends Record<string, unknown> {
  apiId: AgentSessionApiId;
  agentType: AgentType;
  title: string | null;
  status: AgentSessionStatus;
  // TODO: model `inputs` as a discriminated union keyed on `agentType` once the per-agent input shapes
  // are defined (e.g. study_health_check vs study_protocol_parser); it is a loose bag for now.
  inputs: Record<string, unknown> | null;
}

/** A React Flow node specialised with the agent tree's render payload. */
export type AgentGraphNode = GraphCanvasNode<AgentTreeNodeData>;

/** Opens a result node's report, keyed by the node's api id. Injected so mapping stays free of React. */
export type ViewReportHandler = (nodeApiId: AgentTreeNodeApiId) => void;

/** Placeholder position; the layout pass computes the real coordinates. */
const PLACEHOLDER_POSITION = { x: 0, y: 0 } as const;

/**
 * Maps a session status to the corner badge shown on the root card: a tick when completed, a red X when
 * failed/cancelled, and a spinning loader while running. This is the one place agent status becomes
 * presentation — the generic `ReadOnlyNode` only knows about icon + tone + spin.
 */
const statusBadge = (status: AgentSessionStatus): GraphNodeBadge | undefined => {
  switch (status) {
    case 'completed':
      return { icon: RiCheckboxCircleLine, tone: 'success', tooltip: 'Completed' };
    case 'failed':
      return { icon: RiCloseCircleLine, tone: 'error', tooltip: 'Failed' };
    case 'cancelled':
      return { icon: RiCloseCircleLine, tone: 'error', tooltip: 'Cancelled' };
    case 'running':
      return { icon: RiLoader4Line, tone: 'neutral', tooltip: 'Running', spin: true };
    default:
      return undefined;
  }
};

/**
 * Maps a tree-node DTO to a React Flow node. The DTO `variant` (`read_only`/`report`) is both the
 * top-level `node.type` (so React Flow routes to the matching generic component) AND the discriminant
 * the render payload is branched on — a single source of truth, so the payload and the rendered
 * component cannot drift. The structural `type` (`root`/`result`) is kept on `data` for reference.
 * All agent-specific presentation is resolved here: a `read_only` node becomes a `ReadOnlyNode` (a
 * per-agent status title + session-status badge; the agent name lives in the breadcrumb, not the node),
 * a `report` node a `ButtonNode` ("View Report"). The node components hold no agent knowledge.
 */
const mapNode = (
  dto: AgentTreeNodeV1,
  session: AgentTreeSessionV1,
  onViewReport?: ViewReportHandler
): AgentGraphNode => {
  const base = {
    apiId: dto.api_id,
    parentApiId: dto.parent_api_id ?? null,
    type: dto.type,
    sequenceNumber: dto.sequence_number,
  };

  // Exhaustive over the node variant: a new variant added to the DTO union (e.g. question, link)
  // becomes a compile error here until its render payload is defined, rather than silently falling
  // through to a default.
  const data: AgentTreeNodeData = ((): AgentTreeNodeData => {
    switch (dto.variant) {
      case 'read_only':
        return {
          ...base,
          variant: 'read_only',
          title: agentDisplayMap[session.agent_type].title,
          badge: statusBadge(session.status),
        };
      case 'report':
        return {
          ...base,
          variant: 'report',
          label: 'View Report',
          // Disabled until a report opener is wired in; the button becomes actionable once supplied.
          disabled: _isNil(onViewReport),
          onClick: _notNil(onViewReport) ? () => onViewReport(dto.api_id) : undefined,
        };
    }
  })();

  return { id: dto.api_id, type: dto.variant, position: { ...PLACEHOLDER_POSITION }, data };
};

/**
 * Builds a graph node from a live `tree_node_created` stream event. Structural fields come from the
 * event; session-derived presentation (title, status badge) comes from `meta` (the hydrated session
 * held in the engine). Produces the same `AgentGraphNode` shape as {@link mapNode} so streamed and
 * snapshot nodes are indistinguishable to the canvas (and `upsertNodes` dedups any overlap by id).
 */
export const mapTreeNodeCreated = (
  event: TreeNodeCreatedEvent,
  meta: AgentSessionMeta,
  onViewReport?: ViewReportHandler
): AgentGraphNode => {
  const base = {
    apiId: event.node_api_id,
    parentApiId: event.parent_node_api_id ?? null,
    type: event.node_type,
    sequenceNumber: event.sequence_number,
  };

  const data: AgentTreeNodeData = ((): AgentTreeNodeData => {
    switch (event.node_variant) {
      case 'read_only':
        return {
          ...base,
          variant: 'read_only',
          title: agentDisplayMap[meta.agentType].title,
          badge: statusBadge(meta.status),
        };
      case 'report':
        return {
          ...base,
          variant: 'report',
          label: 'View Report',
          disabled: _isNil(onViewReport),
          onClick: _notNil(onViewReport) ? () => onViewReport(event.node_api_id) : undefined,
        };
    }
  })();

  return { id: event.node_api_id, type: event.node_variant, position: { ...PLACEHOLDER_POSITION }, data };
};

/** Derives edges from node parentage; edge id is `{parent}-{child}`, root (no parent) yields none. */
export const deriveAgentEdges = (nodes: ReadonlyArray<AgentGraphNode>): Array<Edge> =>
  nodes.reduce<Array<Edge>>((edges, node) => {
    const parentApiId = node.data.parentApiId;
    if (_notNil(parentApiId)) {
      edges.push({ id: `${parentApiId}-${node.id}`, source: parentApiId, target: node.id });
    }
    return edges;
  }, []);

/**
 * Orders sibling nodes by their server-assigned `sequenceNumber` (creation order), falling back to
 * api-id for a stable tiebreak. `atn_` ids are random FriendlyIds, so sorting by id alone would give
 * an arbitrary left-to-right order once a level has more than one node.
 */
const compareBySequence: SiblingComparator<AgentTreeNodeData> = (a, b) => {
  const bySequence = a.data.sequenceNumber - b.data.sequenceNumber;
  return bySequence !== 0 ? bySequence : StringUtils.compareLexicographic(a.id, b.id);
};

/** GraphCanvasEngine config for the agent session tree: parentage-based edges + sequence-ordered tree layout. */
export const agentTreeGraphConfig: GraphCanvasConfig<AgentTreeNodeData> = {
  deriveEdges: deriveAgentEdges,
  layout: treeLayout(compareBySequence),
};

/**
 * Maps a full `GET /tree` response to the GraphCanvas snapshot shape (meta + nodes). `onViewReport`, if
 * supplied, is wired onto each result node's action so clicking "View Report" opens that node's report.
 */
export const mapTreeToSnapshot = (
  tree: AgentTreeV1,
  onViewReport?: ViewReportHandler
): GraphCanvasSnapshot<AgentTreeNodeData, AgentSessionMeta> => ({
  meta: {
    apiId: tree.session.api_id,
    agentType: tree.session.agent_type,
    title: tree.session.title ?? null,
    status: tree.session.status,
    inputs: tree.session.inputs ?? null,
  },
  nodes: tree.nodes.map((node) => mapNode(node, tree.session, onViewReport)),
});

/**
 * Re-derives each node's status badge for a new session status, returning a fresh node array. The badge
 * is baked into node `data` at map time (not derived live from meta), so a status change must re-map the
 * nodes to move it. Used to settle the root spinner→tick on a terminal stream event WITHOUT a `/tree`
 * refetch — the reconcile that would otherwise refresh badges can fail or fetch a stale `running`
 * snapshot, leaving the badge spinning. Only `read_only` nodes carry a status badge; others pass through.
 */
export const withSessionStatus = (
  nodes: ReadonlyArray<AgentGraphNode>,
  status: AgentSessionStatus
): Array<AgentGraphNode> =>
  nodes.map((node): AgentGraphNode => {
    if (node.data.variant !== 'read_only') {
      return node;
    }
    return { ...node, data: { ...node.data, badge: statusBadge(status) } };
  });
